/**
 * One-line journey summary below Activation Funnel milestones + link to full page.
 */

import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Skeleton, Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';

import apiService from '../../services/api';
import { TimeRangeValue, resolveRangeYmd, toApiToken } from '../common/timeRange';
import { FONT, FONT_MONO, SURFACE } from './funnelBands';
import { JourneySummaryData } from './notificationJourneyTypes';

interface Props {
  /** The page's selection — this strip has no control of its own. */
  range: TimeRangeValue;
}

/** The notifications routes take `range` plus `start`/`end` for a custom span. */
function buildQuery(range: TimeRangeValue): string {
  const params = new URLSearchParams({ range: toApiToken(range) });
  if (range.type === 'custom') {
    const { from, to } = resolveRangeYmd(range);
    params.set('start', from);
    params.set('end', to);
  }
  return params.toString();
}

const JourneyFunnelSummaryStrip: React.FC<Props> = ({ range }) => {
  const [data, setData] = useState<JourneySummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  // A fetch failure is NOT an empty period — the two used to share the
  // "hasn't started sending yet" copy, which reads as a product fact.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await (apiService as { get: (url: string) => Promise<{ data: JourneySummaryData }> }).get(
          `/api/admin/notifications/journey-summary?${buildQuery(range)}`,
        );
        if (!cancelled) { setData(result.data); setFailed(false); }
      } catch {
        if (!cancelled) { setData(null); setFailed(true); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range]);

  if (loading) {
    return <Skeleton variant="rounded" height={52} sx={{ borderRadius: '10px', my: 2 }} />;
  }

  // Null-safe on purpose: a missing field renders '—', never a fabricated 0
  // (design D3.2 — this file was named there and missed in the first wave).
  const total = data?.total_journeys ?? null;
  const converted = data?.by_state?.converted ?? null;
  const fmtCount = (v: number | null) => (v != null ? v.toLocaleString() : '—');
  const fmtPct = (v: number | null | undefined) => (v != null ? `${v.toFixed(1)}%` : '—');
  const pushHint = failed
    ? 'Journey summary could not be loaded — counts are unknown, not zero.'
    : data?.awaiting_migration
      ? 'Journey tables ready — notifications will appear once the journey engine is enabled.'
      : total === 0
        ? 'First Chapter Journey has not started sending yet for this period.'
        : `${fmtCount(total)} active journeys · ${fmtCount(converted)} converted (${fmtPct(data?.conversion_rate)}) · offer redeem ${fmtPct(data?.offer_redemption_rate)}`;

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        px: 2,
        py: 1.25,
        my: 2,
        bgcolor: SURFACE.paper,
        border: `1px solid ${SURFACE.line}`,
        borderRadius: '10px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <NotificationsOutlinedIcon sx={{ color: '#1A5F7A', fontSize: 20, flexShrink: 0 }} />
        <Typography sx={{ fontFamily: FONT, fontSize: '12.5px', color: SURFACE.slate, lineHeight: 1.45 }}>
          <Box component="span" sx={{ fontWeight: 600, color: '#1A5F7A' }}>Push &amp; In-App Journey — </Box>
          {pushHint}
        </Typography>
      </Box>
      <MuiLink
        component={Link}
        to="/dashboard/notification-journey"
        sx={{ fontFamily: FONT_MONO, fontSize: '11px', fontWeight: 600, color: '#146152', whiteSpace: 'nowrap' }}
      >
        View details →
      </MuiLink>
    </Paper>
  );
};

export default JourneyFunnelSummaryStrip;
